/**
 * OKS-503 — Inertia prop shapes for the brand-side EMV dashboard
 * (`/brand/analytics/emv`).
 *
 * Mirrors the server-side `BrandOps::EmvAnalytics::Result` Struct shape
 * 1:1 — keys are snake_case (Inertia JSON round-trip preserves the Ruby
 * serializer's keys). All money is integer **cents** (BIGINT `emv_cents`)
 * — the FE divides by 100 to render USD; there is NO client-side EMV
 * recomputation (PRD §6).
 *
 * # State discriminator (TRD KD-5)
 *
 *   cold_start       → brand has zero tracked posts; all data props null.
 *   empty_in_window  → has posts, none in range; KPIs at 0, arrays empty.
 *   success          → populated. The one-snapshot-day + $0 UX edges are
 *                      VARIANTS within success (dod null + dod_first_day
 *                      true / zero cents), NOT a 4th state value.
 *   error            → controller rescue; all data props null.
 *
 * # Partial-reload (controller `only:` lock)
 *
 *   router.get(brandAnalyticsEmvPath({ range, page }), {}, {
 *     only: ['state', 'range', 'kpis', 'progression', 'missions', 'posts_page'],
 *     preserveScroll: true, preserveState: false,
 *   })
 */

// =========================================================================
// Range presets — mirror `BrandOps::EmvAnalyticsFilters::RANGE_VALUES`
// =========================================================================

export const EMV_RANGE_VALUES = ["7d", "30d", "90d", "all"] as const
export type EmvRange = (typeof EMV_RANGE_VALUES)[number]

// =========================================================================
// State discriminator
// =========================================================================

export const EMV_ANALYTICS_STATES = [
  "cold_start",
  "empty_in_window",
  "success",
  "error",
] as const
export type EmvAnalyticsState = (typeof EMV_ANALYTICS_STATES)[number]

// =========================================================================
// KPI strip
// =========================================================================

export interface EmvKpis {
  /** Σ of each tracked post's latest-in-window emv. Integer cents. */
  total_emv_cents: number
  /**
   * Latest snapshot-day cumulative − previous snapshot-day cumulative.
   * Signed Integer cents. `null` (with `dod_first_day: true`) on the
   * one-snapshot-day edge → renders "—". Direction is the sign; the FE
   * also renders ▲/▼ so color is never the only signal.
   */
  dod_cents: number | null
  /** True when there's no prior snapshot day to diff (first-day edge). */
  dod_first_day: boolean
  /** Count of posts with EMV data in range. */
  tracked_posts: number
  /** total ÷ tracked_posts (integer cents; 0 when no posts). */
  avg_emv_cents: number
}

// =========================================================================
// Progression series
// =========================================================================

export interface EmvProgressionPoint {
  /** UTC day, ISO-8601 (`YYYY-MM-DD`). */
  date: string
  /** Brand cumulative EMV as of this day. Integer cents. */
  cumulative_cents: number
  /** cumulative[D] − cumulative[D−1]. Integer cents (first day diffs vs 0). */
  incremental_cents: number
}

// =========================================================================
// Per-mission roll-up
// =========================================================================

export interface EmvMissionRow {
  id: number
  title: string
  mission_type: string
  /** Tracked posts on this mission in range. */
  post_count: number
  /** Σ posts' latest emv. Integer cents. `$0` missions are listed. */
  total_emv_cents: number
  /** Share of brand total ∈ [0,1] (Float, 4dp). 0 when brand total is 0. */
  share: number
}

// =========================================================================
// Per-post table (paginated)
// =========================================================================

export interface EmvPostRow {
  submission_id: number
  creator_handle: string | null
  creator_avatar_url: string | null
  mission_title: string
  /** Most-recent-in-window snapshot emv (cumulative-to-date). Integer cents. */
  latest_emv_cents: number
  /**
   * Latest − immediately-preceding in-window snapshot emv. Signed Integer
   * cents. `null` (with `dod_first_day: true`) → "—" (first-day edge).
   */
  dod_cents: number | null
  dod_first_day: boolean
}

export interface EmvPagination {
  page: number
  pages: number
  count: number
  prev: number | null
  next: number | null
}

export interface EmvPostsPage {
  rows: EmvPostRow[]
  pagination: EmvPagination
}

// =========================================================================
// Top-level page props
// =========================================================================

export interface EmvAnalyticsProps {
  state: EmvAnalyticsState
  /** URL query echo — drives the range toggle's active state. */
  range: EmvRange
  /** `null` on cold_start / error. */
  kpis: EmvKpis | null
  /** `null` on cold_start / error; `[]` on empty_in_window. */
  progression: EmvProgressionPoint[] | null
  /** `null` on cold_start / error; `[]` on empty_in_window. */
  missions: EmvMissionRow[] | null
  /** `null` on cold_start / error. */
  posts_page: EmvPostsPage | null
}
